import { pal } from '@paramount-streaming/blackbird';
import { bootstrapBrowserPal } from '@paramount-streaming/blackbird-utils';
import { ProjectInfo } from 'library/ts/ProjectInfo';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

// 60% of container height — matches Pluto TV legacy PAL implementation (confirmed by Hebert Morais, Blackbird)
// ref: https://github.com/Pluto-tv/website-monorepo/blob/main/apps/ptv-website-legacy/src/modules/videoPlayer/sagas/videoElement/initializeAvia/streamAdapter/playStream/generateGooglePALNonce.ts
const PAL_PLAYER_HEIGHT_RATIO = 0.6;

/**
 * @copyright © Paramount Streaming
 * @description Wraps the Google PAL (Publisher Advertiser Link) plugin from Pluto TV Blackbird
 * for SmartTag. Intercepts Stitcher manifest requests and injects a signed PAL nonce so that
 * Google Ad Manager can associate ad insertions with a verified playback session.
 *
 * Enabled by default for Stitcher playback — gated on `hasStitcherParams()` and `Pal.enabled`
 * in `shouldBeUsed()`, which also delegates to `#isConsentSuppressed()`. PAL initialization is
 * suppressed when any of these signals is present: CCPA opt-out (`STITCHER.privacy.usPrivacy`),
 * GDPR applicable with no consent string (`STITCHER.privacy.gdpr`/`gdprConsent`), or kids content
 * (`STITCHER.privacy.coppa` or `contentUtilities.isKidsContent()`). P+ is unaffected — this plugin
 * is unreachable without STITCHER params.
 *
 * Smart Tag invokes `bootstrapBrowserPal()` internally each time `shouldBeUsed()` returns true;
 * the helper is idempotent + cached, so repeated calls collapse to a single SDK load.
 * Blackbird's PAL plugin then waits up to `nonceTimeoutMs` (1500ms via `TagConfigPal`) for the
 * nonce before letting the manifest request proceed — enough headroom to capture `paln` on the
 * first manifest even under cold-cache cold-SDK conditions. If the nonce truly doesn't resolve
 * within the cap, the request proceeds without PAL data (playback is never blocked).
 *
 * @see {@link https://playback-docs.webctv.plutostaging.tv/docs/plugins/pal}
 */
export class PalPlugin extends AviaPlugin implements IAviaPlugin {
	package = pal as unknown as TPluginConfigFactory;
	namespaceKey = 'plugin_pal';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'PalPlugin'
			}
		);
	}

	/**
	 * @description Returns true when STITCHER params are present, Pal is enabled in tag config,
	 * and no consent signal independently requires suppression.
	 *
	 * Consent evaluation is delegated to `#isConsentSuppressed()` — see that method for gate details.
	 * Also lazy-fires `bootstrapBrowserPal()` (idempotent + cached) so `window.google.pal` is
	 * installed in parallel with the rest of plugin init.
	 * @returns Whether PAL SDK should be initialized for this playback session.
	 */
	shouldBeUsed(): boolean {
		if ( !this.hasStitcherParams() || !this.tag?.model?.config?.tag?.Pal?.enabled ) {
			return false;
		}

		if ( this.#isConsentSuppressed() ) {
			return false;
		}

		// bootstrapBrowserPal catches internally and resolves false on failure — surface that
		// through Smart Tag's logger so a failed bootstrap is observable, not just console.warn.
		bootstrapBrowserPal( {
			palScriptUrl: this.tag?.model?.config?.tag?.Pal?.params?.palScriptUrl
		} ).then( ( ready ) => {
			if ( !ready ) {
				this?.logger?.warn?.( 'PalPlugin.shouldBeUsed: bootstrapBrowserPal resolved false' );
			}
		} );

		return true;
	}

	/**
	 * @description Safe to reinitialize mid-session (PLBCK-8475): `shouldBeUsed()` (consent/
	 * COPPA gates) and `getFactoryParams()` (`continuousPlayback` live-vs-VOD signal) both
	 * already read current tag state fresh on every call — they just were never being
	 * re-invoked before a stream-swap reuse. Blackbird's teardown (`destroy()`:
	 * `removeListeners()`, `palManager.clear()`, `player.removeHook()`) is fully synchronous,
	 * same safety profile as `AdsPlugin`. Reinitializing does not reload the PAL SDK script or
	 * reset `window.google.pal`/`NonceLoader` — `bootstrapBrowserPal()` caches those at the
	 * module/window level, not per plugin instance.
	 * @returns {boolean}
	 */
	isReinitializable(): boolean {
		return true;
	}

	/**
	 * @description Returns true when any consent signal independently requires PAL suppression.
	 * Each gate is sufficient on its own — all read from STITCHER.privacy, the canonical consent
	 * passthrough for the Pluto/Stitcher path.
	 *
	 * - CCPA: IAB US Privacy string position 2 === 'Y' (opted out). Falls back to USER.usPrivacy.
	 *   typeof guard required — index signature allows number, indexing only meaningful on string.
	 * - GDPR: applicable (gdpr === 1) with no TCF v2 consent string. Number() coercion guards
	 *   against string '1' at runtime; gdprConsent falsy check covers '' and undefined.
	 * - Kids: coppa === '1' or 1 from TN Web (index signature allows both). isKidsContent() is a
	 *   fallback reading from the Stitcher metadata pipeline independently of the privacy object.
	 */
	#isConsentSuppressed(): boolean {
		const privacy = this.params?.STITCHER?.privacy;

		const usPrivacy = privacy?.usPrivacy ?? this.params?.USER?.usPrivacy;
		if ( typeof usPrivacy === 'string' && usPrivacy[ 2 ] === 'Y' ) {
			this?.logger?.log( 'PalPlugin.shouldBeUsed: suppressed — CCPA opt-out' );
			return true;
		}

		if ( Number( privacy?.gdpr ) === 1 && !privacy?.gdprConsent ) {
			this?.logger?.log( 'PalPlugin.shouldBeUsed: suppressed — GDPR applies, no consent string' );
			return true;
		}

		if ( privacy?.coppa === '1' || privacy?.coppa === 1 || this.tag?.model?.contentUtilities?.isKidsContent() ) {
			this?.logger?.log( 'PalPlugin.shouldBeUsed: suppressed — kids content' );
			return true;
		}

		return false;
	}

	/**
	 * @description Returns parameters required to initialize pal from Pluto TV Blackbird.
	 * Reads from tag config (Pal.params) and maps to PalPluginOptionsInterface.
	 *
	 * Three fields are resolved dynamically at plugin instantiation time (per stream), with
	 * page-supplied values taking precedence:
	 * - playerVersion: Smart Tag release version when not host-supplied
	 * - continuousPlayback: true for live streams, false for VOD
	 * - getPlayerSize: returns container width + 60% of container height
	 *
	 * @see {@link https://playback-docs.webctv.plutostaging.tv/docs/plugins/pal}
	 */
	getFactoryParams() {
		const params = this.tag?.model?.config?.tag?.Pal?.params;
		const container = this.tag?.view?.container;
		const isLive = !!this.tag?.model?.contentUtilities?.isLive();

		const palConfig = {
			...params,
			playerVersion: params?.playerVersion ?? ProjectInfo.release_version,
			continuousPlayback: params?.continuousPlayback ?? isLive, // live = lean-back autoplay (true); VOD = user-initiated (false) — signals ad inventory context to Google
			getPlayerSize: params?.getPlayerSize ?? ( container ? () => ( {
				width: container.clientWidth,
				height: Math.round( container.clientHeight * PAL_PLAYER_HEIGHT_RATIO )
			} ) : undefined ),
			ppid: this.tag?.model?.siteUtilities?.siteSessionData?.ppid
		};

		return {
			...super.getFactoryParams(),
			palConfig,
			stitchUrlMatcher: params?.stitchUrlMatcher
		};
	}
}